import React, { useState, useEffect } from 'react';
import {
    Box, Card, CardContent, Typography, Button, Chip, Grid, Stack,
    Divider, Alert, Skeleton,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    LocalShipping as SupplierIcon,
    Receipt as PurchaseOrderIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
    Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import supplierService from '../../../services/supplierService';
import purchaseOrderService from '../../../services/purchaseOrderService';
import AppDataGrid from '../../common/AppDataGrid';
import GridActionsCell from '../../common/GridActionsCell';
import { useCurrency } from '../../../hooks/useCurrency';

const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('fr-DZ', {
        year: 'numeric', month: 'short', day: 'numeric',
    });
};

const poStatusColors = { DRAFT: 'default', SENT: 'info', RECEIVED: 'success', CANCELLED: 'error' };

const InfoCardSkeleton = () => (
    <Stack spacing={1.5}>
        {[...Array(6)].map((_, i) => (
            <Box key={i}>
                <Skeleton variant="text" width={80} height={16} />
                <Skeleton variant="text" width="60%" height={24} />
            </Box>
        ))}
    </Stack>
);

const SupplierDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();

    const { format: fmt } = useCurrency();
    const [supplier, setSupplier] = useState(null);
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchSupplier();
        fetchPurchaseOrders();
    }, [id]);

    const fetchSupplier = async () => {
        setLoading(true);
        try {
            const data = await supplierService.getSupplierById(id);
            setSupplier(data);
        } catch (err) {
            setError(err.response?.data?.message || t('suppliers.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const fetchPurchaseOrders = async () => {
        setOrdersLoading(true);
        try {
            const data = await purchaseOrderService.getPurchaseOrdersBySupplier(id);
            setPurchaseOrders(data || []);
        } catch {
            setPurchaseOrders([]);
        } finally {
            setOrdersLoading(false);
        }
    };

    const columns = [
        { field: 'poNumber', headerName: t('purchaseOrders.poNumber'), flex: 1, minWidth: 160 },
        {
            field: 'status',
            headerName: t('common.status'),
            width: 140,
            cellRenderer: (params) => (
                <Chip
                    label={t(`purchaseOrders.status.${params.value}`, params.value)}
                    color={poStatusColors[params.value] || 'default'}
                    size="small"
                />
            ),
        },
        {
            field: 'totalAmount',
            headerName: t('purchaseOrders.totalAmount'),
            width: 150,
            cellRenderer: (params) => fmt(params.value),
        },
        {
            field: 'expectedDeliveryDate',
            headerName: t('purchaseOrders.expectedDate'),
            width: 140,
            cellRenderer: (params) => fmtDate(params.value),
        },
        {
            field: 'createdAt',
            headerName: t('common.createdAt'),
            width: 140,
            cellRenderer: (params) => fmtDate(params.value),
        },
        {
            field: 'actions',
            headerName: t('common.actions'),
            width: 100,
            sortable: false,
            cellRenderer: (params) => (
                <GridActionsCell
                    actions={[
                        {
                            key: 'view',
                            icon: ViewIcon,
                            title: t('common.details'),
                            color: 'info',
                            onClick: () => navigate(`/purchase-orders/${params.data.id}`),
                        },
                    ]}
                />
            ),
        },
    ];

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/suppliers')} sx={{ mt: 2 }}>
                    {t('common.back')}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/suppliers')}>
                        {t('common.back')}
                    </Button>
                    {loading ? (
                        <Skeleton variant="text" width={220} height={40} />
                    ) : (
                        <>
                            <Typography variant="h4" fontWeight="bold">{supplier.name}</Typography>
                            <Chip
                                icon={supplier.isActive ? <ActiveIcon /> : <InactiveIcon />}
                                label={supplier.isActive ? t('common.active') : t('common.inactive')}
                                color={supplier.isActive ? 'success' : 'default'}
                                size="small"
                            />
                        </>
                    )}
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    disabled={loading}
                    onClick={() => navigate(`/suppliers/edit/${id}`)}
                >
                    {t('common.edit')}
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* Left: supplier info */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <SupplierIcon color="primary" />
                                <Typography variant="h6">{t('suppliers.supplier')}</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {loading ? (
                                <InfoCardSkeleton />
                            ) : (
                                <Stack spacing={1.5}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">{t('suppliers.contactName')}</Typography>
                                        <Typography variant="body1" fontWeight="medium">{supplier.contactName || '—'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">{t('suppliers.email')}</Typography>
                                        <Typography variant="body1" fontWeight="medium">{supplier.email || '—'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">{t('suppliers.phone')}</Typography>
                                        <Typography variant="body1" fontWeight="medium">{supplier.phone || '—'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">{t('common.address')}</Typography>
                                        <Typography variant="body1" fontWeight="medium">{supplier.address || '—'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('suppliers_ext.cityCountry')}
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {supplier.city && supplier.country
                                                ? `${supplier.city}, ${supplier.country}`
                                                : supplier.city || supplier.country || '—'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">{t('suppliers.stores')}</Typography>
                                        <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(supplier.storeNames || []).length > 0
                                                ? supplier.storeNames.map((storeName) => (
                                                    <Chip key={storeName} label={storeName} size="small" variant="outlined" />
                                                ))
                                                : <Typography variant="body2">—</Typography>}
                                        </Box>
                                    </Box>
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right: purchase order history */}
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <PurchaseOrderIcon color="primary" />
                                <Typography variant="h6">
                                    {t('purchaseOrders.title')}
                                    {!ordersLoading && (
                                        <Chip
                                            label={purchaseOrders.length}
                                            size="small"
                                            sx={{ ml: 1 }}
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    )}
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <AppDataGrid
                                rows={purchaseOrders}
                                columns={columns}
                                loading={ordersLoading}
                                pageSize={10}
                                pageSizeOptions={[10, 25, 50]}
                                getRowId={(row) => row.id}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SupplierDetails;
